(() => {
  const productCache = new Map();
  let activeRequest;

  const formatPrice = (value) => {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return '';

    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const collectionProductsEndpoint = (collectionUrl) => {
    const url = new URL(collectionUrl, window.location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/products.json`;
    url.search = 'limit=12';
    return url;
  };

  const createProductCard = (product) => {
    const variant = product.variants && product.variants[0];
    const link = document.createElement('a');

    link.className = 'bb-editorial-home__palette-modal-card';
    link.href = `/products/${encodeURIComponent(product.handle)}`;

    if (product.images && product.images[0] && product.images[0].src) {
      const image = document.createElement('img');
      image.className = 'bb-editorial-home__palette-modal-image';
      image.src = product.images[0].src;
      image.alt = product.images[0].alt || product.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      link.append(image);
    }

    const title = document.createElement('span');
    title.className = 'bb-editorial-home__palette-modal-card-title';
    title.textContent = product.title;
    link.append(title);

    if (variant && variant.price) {
      const pricing = document.createElement('span');
      pricing.className = 'bb-editorial-home__palette-modal-price';

      if (variant.compare_at_price && Number.parseFloat(variant.compare_at_price) > Number.parseFloat(variant.price)) {
        const compare = document.createElement('s');
        compare.className = 'bb-editorial-home__palette-modal-compare';
        compare.textContent = formatPrice(variant.compare_at_price);
        pricing.append(compare);
      }

      const price = document.createElement('strong');
      price.textContent = formatPrice(variant.price);
      pricing.append(price);
      link.append(pricing);
    }

    return link;
  };

  const initializePaletteModal = (root) => {
    if (root.dataset.paletteModalReady === 'true') return;

    const triggers = Array.from(root.querySelectorAll('[data-palette-trigger]'));
    const dialog = root.querySelector('[data-palette-modal]');
    if (!triggers.length || !dialog) return;

    const title = dialog.querySelector('[data-palette-modal-title]');
    const status = dialog.querySelector('[data-palette-modal-status]');
    const grid = dialog.querySelector('[data-palette-modal-grid]');
    const collectionLink = dialog.querySelector('[data-palette-modal-link]');
    const closeButton = dialog.querySelector('[data-palette-modal-close]');

    const close = () => {
      if (dialog.open) dialog.close();
    };

    const open = async (trigger) => {
      const collectionUrl = trigger.dataset.paletteCollectionUrl;
      const collectionTitle = trigger.dataset.paletteTitle || trigger.textContent.trim();
      if (!collectionUrl) {
        window.location.assign(trigger.href);
        return;
      }

      title.textContent = collectionTitle;
      status.textContent = 'Loading matching lights…';
      grid.replaceChildren();
      collectionLink.href = trigger.href;

      if (!dialog.open) dialog.showModal();

      const endpoint = collectionProductsEndpoint(collectionUrl);
      const cacheKey = endpoint.href;
      const request = new AbortController();
      if (activeRequest) activeRequest.abort();
      activeRequest = request;

      try {
        let products = productCache.get(cacheKey);
        if (!products) {
          const response = await fetch(endpoint, {
            headers: { Accept: 'application/json' },
            signal: request.signal,
          });

          if (!response.ok) throw new Error(`Unable to load collection (${response.status})`);

          const payload = await response.json();
          products = Array.isArray(payload.products) ? payload.products : [];
          productCache.set(cacheKey, products);
        }

        if (!dialog.open || request.signal.aborted || activeRequest !== request) return;

        if (!products.length) {
          status.textContent = 'No matching lights are available right now.';
          return;
        }

        const fragment = document.createDocumentFragment();
        products.forEach((product) => fragment.append(createProductCard(product)));
        grid.append(fragment);
        status.textContent = `${products.length} matching lights`;
      } catch (error) {
        if (error.name === 'AbortError') return;
        status.textContent = 'We could not load these lights. Use the collection link below.';
      }
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        open(trigger);
      });
    });

    closeButton.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    root.dataset.paletteModalReady = 'true';
  };

  const initializeTestimonialsRail = (root) => {
    if (root.dataset.testimonialRailReady === 'true') return;

    const rail = root.querySelector('.bb-editorial-home__testimonials-rail');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!rail || rail.children.length < 2 || reducedMotion.matches) {
      root.dataset.testimonialRailReady = 'true';
      return;
    }

    let animationFrame;
    let lastTimestamp;
    let isPaused = false;
    let isIntersecting = false;
    let restartTimeout;
    const pixelsPerSecond = 24;

    const pause = () => {
      isPaused = true;
      rail.classList.remove('is-autoscrolling');
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
    };

    const resume = () => {
      isPaused = false;
      lastTimestamp = undefined;
      rail.classList.add('is-autoscrolling');
      if (isIntersecting && !animationFrame) {
        animationFrame = window.requestAnimationFrame(move);
      }
    };

    const resumeAfterInteraction = () => {
      pause();
      window.clearTimeout(restartTimeout);
      restartTimeout = window.setTimeout(resume, 2400);
    };

    const move = (timestamp) => {
      if (!isPaused && isIntersecting && document.visibilityState === 'visible') {
        const maximumScroll = rail.scrollWidth - rail.clientWidth;

        if (maximumScroll > 0 && lastTimestamp) {
          const nextPosition = rail.scrollLeft + ((timestamp - lastTimestamp) * pixelsPerSecond) / 1000;

          if (nextPosition >= maximumScroll - 1) {
            pause();
            restartTimeout = window.setTimeout(() => {
              rail.scrollLeft = 0;
              resume();
            }, 1400);
          } else {
            rail.scrollLeft = nextPosition;
          }
        }
      }

      lastTimestamp = timestamp;
      if (isIntersecting && !isPaused) {
        animationFrame = window.requestAnimationFrame(move);
      } else {
        animationFrame = undefined;
      }
    };

    rail.addEventListener('pointerenter', pause);
    rail.addEventListener('pointerleave', resume);
    rail.addEventListener('focusin', pause);
    rail.addEventListener('focusout', (event) => {
      if (!rail.contains(event.relatedTarget)) resume();
    });
    rail.addEventListener('pointerdown', resumeAfterInteraction);
    rail.addEventListener('wheel', resumeAfterInteraction, { passive: true });
    rail.addEventListener('touchstart', resumeAfterInteraction, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !isPaused && isIntersecting) {
        lastTimestamp = undefined;
        if (!animationFrame) animationFrame = window.requestAnimationFrame(move);
      } else if (document.visibilityState !== 'visible') {
        if (animationFrame) {
          window.cancelAnimationFrame(animationFrame);
          animationFrame = undefined;
        }
      }
    });

    root.dataset.testimonialRailReady = 'true';

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          isIntersecting = entry.isIntersecting;
          if (isIntersecting) {
            resume();
          } else {
            pause();
          }
        });
      }, { rootMargin: '120px' });
      observer.observe(rail);
    } else {
      isIntersecting = true;
      resume();
    }
  };

  const initializeSpotlight = (root) => {
    const videoSections = Array.from(
      root.querySelectorAll('.bb-editorial-home__spotlight, .bb-editorial-home__founder')
    );
    if (!videoSections.length) return;

    videoSections.forEach((section) => {
      if (section.dataset.videoRailReady === 'true') return;

      const cards = Array.from(section.querySelectorAll('.bb-editorial-home__spotlight-card'));
      const videos = Array.from(section.querySelectorAll('[data-spotlight-video]'));

      // IntersectionObserver for lazy loading and autoplay muted when in view
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              const card = video.closest('.bb-editorial-home__spotlight-card');
              if (entry.isIntersecting) {
                if (video.dataset.src && !video.src) {
                  video.src = video.dataset.src;
                  video.load();
                }
                if (card && card.dataset.userPaused === 'true') {
                  return;
                }
                const playPromise = video.play();
                if (playPromise !== undefined) {
                  playPromise.catch(() => {});
                }
              } else {
                if (!video.paused) {
                  video.pause();
                }
              }
            });
          },
          { rootMargin: '80px 0px', threshold: 0.2 }
        );

        videos.forEach((video) => observer.observe(video));
      } else {
        videos.forEach((video) => {
          if (video.dataset.src && !video.src) {
            video.src = video.dataset.src;
            video.load();
          }
          video.play().catch(() => {});
        });
      }

      // Sync play/pause state classes on each card
      videos.forEach((video) => {
        const card = video.closest('.bb-editorial-home__spotlight-card');
        if (!card) return;

        const syncPlayState = () => {
          if (video.paused) {
            card.classList.add('is-paused');
            card.classList.remove('is-playing');
          } else {
            card.classList.add('is-playing');
            card.classList.remove('is-paused');
          }
        };

        video.addEventListener('play', syncPlayState);
        video.addEventListener('pause', syncPlayState);
        video.addEventListener('ended', syncPlayState);

        syncPlayState();
      });

      // Helper to toggle play / pause for a card
      const togglePlayPause = (card) => {
        const video = card.querySelector('video');
        if (!video) return;

        if (video.dataset.src && !video.src) {
          video.src = video.dataset.src;
          video.load();
        }

        if (video.paused) {
          card.dataset.userPaused = 'false';
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        } else {
          card.dataset.userPaused = 'true';
          video.pause();
        }
      };

      // Play/Pause button click handlers
      const playButtons = Array.from(section.querySelectorAll('[data-spotlight-play]'));
      playButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const card = btn.closest('.bb-editorial-home__spotlight-card');
          if (card) togglePlayPause(card);
        });
      });

      // Card click handler (click anywhere on card outside links and buttons to toggle playback)
      cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          if (
            e.target.closest('a') ||
            e.target.closest('[data-spotlight-sound]') ||
            e.target.closest('[data-spotlight-play]')
          ) {
            return;
          }
          togglePlayPause(card);
        });
      });

      // Sound toggle buttons
      const soundButtons = Array.from(section.querySelectorAll('[data-spotlight-sound]'));
      soundButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const card = btn.closest('.bb-editorial-home__spotlight-card');
          const video = card && card.querySelector('video');
          if (!video) return;

          const isCurrentlyMuted = video.muted;
          if (isCurrentlyMuted) {
            // Unmute this video, mute all other videos across page
            document.querySelectorAll('[data-spotlight-video]').forEach((v) => {
              v.muted = true;
              const otherBtn = v.closest('.bb-editorial-home__spotlight-card')?.querySelector('[data-spotlight-sound]');
              if (otherBtn) otherBtn.setAttribute('aria-pressed', 'false');
            });
            video.muted = false;
            btn.setAttribute('aria-pressed', 'true');
          } else {
            video.muted = true;
            btn.setAttribute('aria-pressed', 'false');
          }
        });
      });

      section.dataset.videoRailReady = 'true';
    });
  };

  const initialize = () => {
    document.querySelectorAll('.bb-editorial-home').forEach((root) => {
      initializePaletteModal(root);
      initializeTestimonialsRail(root);
      initializeSpotlight(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches('.bb-editorial-home')
      ? event.target
      : event.target.querySelector('.bb-editorial-home');
    if (root) {
      initializePaletteModal(root);
      initializeTestimonialsRail(root);
      initializeSpotlight(root);
    }
  });
})();
